import { useState, useEffect, useRef, useCallback } from 'react';
import { clearAllCache } from '../services/cache';
import {
  fetchChurnAnalysis,
  fetchStaleUsers,
  fetchStaleAccounts,
} from '../services/retentionData';
import type { ChurnAnalysis, StaleUsersData, StaleAccountsData } from '../types/retention';

interface UseRetentionDataResult {
  churnAnalysis: ChurnAnalysis | null;
  staleUsers: StaleUsersData | null;
  staleAccounts: StaleAccountsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRetentionData(): UseRetentionDataResult {
  const [churnAnalysis, setChurnAnalysis] = useState<ChurnAnalysis | null>(null);
  const [staleUsers, setStaleUsers] = useState<StaleUsersData | null>(null);
  const [staleAccounts, setStaleAccounts] = useState<StaleAccountsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    clearAllCache();
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    async function loadAll() {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        fetchChurnAnalysis(controller.signal),
        fetchStaleUsers(controller.signal),
        fetchStaleAccounts(controller.signal),
      ]);

      if (cancelled) return;

      const [churnRes, staleUsersRes, staleAccountsRes] = results;

      if (churnRes.status === 'fulfilled') setChurnAnalysis(churnRes.value);
      if (staleUsersRes.status === 'fulfilled') setStaleUsers(staleUsersRes.value);
      if (staleAccountsRes.status === 'fulfilled') setStaleAccounts(staleAccountsRes.value);

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === results.length) {
        const firstErr = (failures[0] as PromiseRejectedResult).reason;
        setError(firstErr instanceof Error ? firstErr.message : 'Failed to load retention data');
      }

      setLoading(false);
    }

    loadAll();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [refreshKey]);

  return { churnAnalysis, staleUsers, staleAccounts, loading, error, refetch };
}
